/**
 * Jerarquía de claves de la CUENTA (raíz del zero-knowledge).
 *
 *   contraseña ──Argon2id──▶ claveMaestra
 *      claveMaestra ──HKDF("master-wrap")──▶ wrapKey ──envuelve──▶ claveDeCuenta (aleatoria)
 *      claveMaestra ──HKDF("auth-verifier")─▶ authHash (al servidor, unidireccional)
 *   claveDeCuenta ──envuelve──▶ clavePrivadaECDH  (la pública viaja en claro)
 *   claveDeCuenta ──deriva──▶ claves de vaults personales / abre sobres compartidos
 *
 * La claveDeCuenta es la clave de datos raíz: se genera aleatoria (no deriva de
 * la contraseña) para que CAMBIAR LA CONTRASEÑA solo re-envuelva la claveDeCuenta
 * (no obligue a re-cifrar todo el vault). La contraseña nunca cifra datos
 * directamente; la claveMaestra solo envuelve la claveDeCuenta.
 */

import { base64ToBytes, bytesToBase64 } from "./encoding";
import { generateKeyPair } from "./asymmetric";
import {
  type Argon2Params,
  deriveAuthHash,
  deriveMasterKey,
  deriveMasterWrapKey,
  KDF_PRESETS,
  type KdfConfig,
  newKdfSaltBytes,
} from "./kdf";
import { openBase64, randomSymmetricKey, sealBase64 } from "./primitives";
import type { ProtectedAccount } from "./types";

const AAD_ACCOUNT_KEY = "boveda:account-key:v1";
const AAD_PRIVATE_KEY = "boveda:private-key:v1";

/** Estado desbloqueado en memoria (nunca se serializa). */
export interface UnlockedAccount {
  readonly userId: string;
  /** Clave de datos raíz (32 bytes). SECRETA, solo en memoria. */
  readonly accountKey: Uint8Array;
  /** Clave privada ECDH (PKCS#8 base64). SECRETA, solo en memoria. */
  readonly privateKey: string;
  readonly publicKey: string;
}

export interface CreatedAccount {
  /** Lo único que sube al servidor. */
  readonly protectedAccount: ProtectedAccount;
  /** Estado desbloqueado resultante (queda en cliente). */
  readonly unlocked: UnlockedAccount;
}

/**
 * Crea una cuenta zero-knowledge a partir de la contraseña maestra. Genera la
 * clave de cuenta y el par ECDH, y produce el `ProtectedAccount` (blobs) para
 * el servidor. El servidor NUNCA ve la contraseña, la clave maestra ni la clave
 * de cuenta en claro.
 */
export async function createAccount(
  userId: string,
  password: string,
  params: Argon2Params = KDF_PRESETS.sensitive,
): Promise<CreatedAccount> {
  const saltBytes = newKdfSaltBytes();
  const kdf: KdfConfig = { algorithm: "argon2id", params, salt: bytesToBase64(saltBytes), v: 1 };

  const masterKey = deriveMasterKey(password, saltBytes, params);
  const wrapKey = await deriveMasterWrapKey(masterKey);
  const authHash = await deriveAuthHash(masterKey);

  const accountKey = randomSymmetricKey();
  const keyPair = await generateKeyPair();

  const protectedAccountKey = await sealBase64(wrapKey, accountKey, AAD_ACCOUNT_KEY);
  const protectedPrivateKey = await sealBase64(
    accountKey,
    base64ToBytes(keyPair.privateKey),
    AAD_PRIVATE_KEY,
  );

  return {
    protectedAccount: {
      userId,
      kdf,
      protectedAccountKey,
      protectedPrivateKey,
      publicKey: keyPair.publicKey,
      authHash: bytesToBase64(authHash),
    },
    unlocked: {
      userId,
      accountKey,
      privateKey: keyPair.privateKey,
      publicKey: keyPair.publicKey,
    },
  };
}

/** Deriva el authHash desde la contraseña (lo que se envía al servidor al hacer login). */
export async function computeAuthHash(password: string, kdf: KdfConfig): Promise<string> {
  const masterKey = deriveMasterKey(password, base64ToBytes(kdf.salt), kdf.params);
  return bytesToBase64(await deriveAuthHash(masterKey));
}

/**
 * Desbloquea la cuenta con la contraseña maestra y el `ProtectedAccount`
 * traído del servidor. Si la contraseña es incorrecta, el AEAD open falla
 * (no hace falta preguntar al servidor: la prueba es criptográfica).
 */
export async function unlockAccount(
  password: string,
  account: ProtectedAccount,
): Promise<UnlockedAccount> {
  const masterKey = deriveMasterKey(password, base64ToBytes(account.kdf.salt), account.kdf.params);
  const wrapKey = await deriveMasterWrapKey(masterKey);
  let accountKey: Uint8Array;
  try {
    accountKey = await openBase64(wrapKey, account.protectedAccountKey, AAD_ACCOUNT_KEY);
  } catch {
    throw new Error("invalid master password");
  }
  const privateKeyBytes = await openBase64(
    accountKey,
    account.protectedPrivateKey,
    AAD_PRIVATE_KEY,
  );
  return {
    userId: account.userId,
    accountKey,
    privateKey: bytesToBase64(privateKeyBytes),
    publicKey: account.publicKey,
  };
}

/**
 * Cambia la contraseña maestra: re-envuelve la MISMA clave de cuenta con la
 * nueva wrap-key (los datos no se re-cifran). Devuelve los campos actualizados
 * del `ProtectedAccount`. Requiere la cuenta ya desbloqueada.
 */
export async function changePassword(
  unlocked: UnlockedAccount,
  newPassword: string,
  params: Argon2Params = KDF_PRESETS.sensitive,
): Promise<Pick<ProtectedAccount, "kdf" | "protectedAccountKey" | "authHash">> {
  const saltBytes = newKdfSaltBytes();
  const kdf: KdfConfig = { algorithm: "argon2id", params, salt: bytesToBase64(saltBytes), v: 1 };
  const masterKey = deriveMasterKey(newPassword, saltBytes, params);
  const wrapKey = await deriveMasterWrapKey(masterKey);
  const authHash = await deriveAuthHash(masterKey);
  const protectedAccountKey = await sealBase64(wrapKey, unlocked.accountKey, AAD_ACCOUNT_KEY);
  return { kdf, protectedAccountKey, authHash: bytesToBase64(authHash) };
}
